import { useNavigate } from 'react-router-dom';

import CalendarIcon from '@lib/core/src/assets/empty-state-icons/calendar.svg?react';
import CalendarSvg from '@lib/core/src/assets/images/calendar-widget.svg';
import { EmptyState } from '@lib/core/src/components/EmptyState';
import Skeleton from '@lib/core/src/components/Skeleton';
import { H2, SH2 } from '@lib/core/src/styledComponents/StyledComponents';
import dayjs from 'dayjs';

import { CalendarMeetingDto } from '@/api/generated';
import CalendarEvent from '@/components/CalendarEvents/CalendarEvent/CalendarEvent';
import { EVENT_HEIGHT } from '@/components/CalendarEvents/CalendarEvent/CalendarEventStyles';
import { useCalendarEventsLogic } from '@/components/CalendarEvents/hooks/useCalendarEventsLogic';
import { CalendarEventWithSchedule } from '@/components/CalendarEvents/types';
import RoutesMap from '@/enums/RoutesMap';
import useCalendar from '@/hooks/useCalendar';
import { ViewAllButton } from '@/pages/HomePage/HomeStyles';
import {
  Container,
  Row,
  TitleContainer,
  RightContainer,
  ImageContainer,
  TitleAndViewAllContainer,
  Image,
  ScrollContainer
} from '@/pages/HomePage/components/CalendarMeetingsStyles';
import { useIsSmallScreen } from '@/utils/mediaUtil';

import { CalendarInviteButton } from './CalendarViewStyles';

const CalendarView = () => {
  const {
    isLoadingInviteAll,
    eventListWithSchedule,
    meetingList,
    shouldShowInviteToAllButton,
    handleClickInviteToAll,
    scheduleBot,
    removeBot,
    setEventListWithSchedule,
    isLoading
  } = useCalendarEventsLogic(dayjs());

  const navigate = useNavigate();
  const isSmallScreen = useIsSmallScreen();
  const today = dayjs();
  const startOfDay = today.startOf('day').valueOf();
  const endOfDay = today.endOf('day').valueOf();
  const { eventList } = useCalendar(startOfDay, endOfDay);

  return (
    <Container>
      <TitleAndViewAllContainer>
        <TitleContainer>
          <Row>
            <H2>Calendar</H2>
            {!isSmallScreen && (
              <ViewAllButton onClick={() => navigate(RoutesMap.Calendar)}>View all</ViewAllButton>
            )}
          </Row>
          <SH2>{eventList?.length || 0} Events Today</SH2>
        </TitleContainer>
        <ImageContainer>
          {shouldShowInviteToAllButton && (
            <CalendarInviteButton onClick={handleClickInviteToAll} disabled={isLoadingInviteAll}>
              Invite to all
            </CalendarInviteButton>
          )}
          <Image imageUrl={CalendarSvg} />
        </ImageContainer>
      </TitleAndViewAllContainer>
      <RightContainer>
        <ScrollContainer>
          {isLoading ? (
            <Skeleton count={4} rowHeight={EVENT_HEIGHT} />
          ) : (
            <RenderCalendarEvents
              eventListWithSchedule={eventListWithSchedule}
              isLoadingInviteAll={isLoadingInviteAll}
              setEventListWithSchedule={setEventListWithSchedule}
              meetingList={meetingList}
              scheduleBot={scheduleBot}
              removeBot={removeBot}
            />
          )}
        </ScrollContainer>
      </RightContainer>
    </Container>
  );
};

export default CalendarView;

interface RenderCalendarEventsProps {
  eventListWithSchedule: CalendarEventWithSchedule[];
  isLoadingInviteAll: boolean;
  setEventListWithSchedule: React.Dispatch<React.SetStateAction<CalendarEventWithSchedule[]>>;
  meetingList: CalendarMeetingDto[];
  scheduleBot: (event: CalendarEventWithSchedule) => Promise<void>;
  removeBot: (event: CalendarEventWithSchedule) => Promise<void>;
}

const RenderCalendarEvents = ({
  eventListWithSchedule,
  isLoadingInviteAll,
  setEventListWithSchedule,
  meetingList,
  scheduleBot,
  removeBot
}: RenderCalendarEventsProps) => {
  if (eventListWithSchedule.length === 0) {
    return <EmptyState text="No events today" icon={<CalendarIcon />} />;
  }

  return eventListWithSchedule.map(event => (
    <CalendarEvent
      isLoadingInviteAll={isLoadingInviteAll}
      key={event.id}
      event={event}
      setEventListWithSchedule={setEventListWithSchedule}
      meeting={meetingList?.find(meeting => meeting.calendarEventId === event.id)}
      scheduleBot={scheduleBot}
      removeBot={removeBot}
    />
  ));
};
